import * as PropTypes from 'prop-types';

import {ARCHIVABLE_ITEM_REST_PROPS} from 'archive/ArchiveConstants';
import {HasPoliceablePermissions} from 'auth/PermissionConstants';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  AutomationInputFileConfig,
  AutomationOutputFileConfig,
} from 'lab-automation/automation-files/LabAutomationConstants';
import {DataTableColumn} from 'notebook/data-tables/DataTableConstants';
import {DATA_TABLE_COLUMN_SHAPE} from 'notebook/structured-tables/StructuredTableConstants';
import * as OrganizationConstants from 'orgs/OrganizationConstants';
import {getLegacyRequestObjectName} from 'requests/RequestConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import * as SidebarConstants from 'sidebar/SidebarConstants';
import {Optional} from 'util/Types';
import {SAMPLE_GROUP_SCHEMA_SHAPE, SampleGroupSchema} from 'workflows/WorkflowConstants';

import {AssaySchemaGraphQL} from '../../../graphql-fragments/assays/__generated/AssaySchemaGraphQL';
import * as AssaySchemaDAO from './AssaySchemaDAO';

export {LegacyAssaySchemaType as ASSAY_TYPES} from 'graphql-client/__generated/graphqlGlobalTypes';

const {Section} = SidebarConstants;

export const SCHEMA_TYPE_NAME = {
  [LegacyAssaySchemaType.ASSAY_RESULT]: 'result',
  [LegacyAssaySchemaType.ASSAY_RUN]: 'run',
  [LegacyAssaySchemaType.ASSAY_REQUEST]: 'request',
};

export function getJsonUrl(
  schemaType: LegacyAssaySchemaType.ASSAY_RESULT | LegacyAssaySchemaType.ASSAY_RUN,
  schemaId?: string
): string {
  return schemaId
    ? AssaySchemaDAO.assaySchemaURL(schemaType, schemaId, true)
    : AssaySchemaDAO.createAssaySchemaURLJSON(schemaType);
}

export const ASSAY_SCHEMA_SIDEBAR_SECTIONS = {
  ASSAY_RESULT: Section.ASSAY_RESULT_SCHEMAS,
  ASSAY_RUN: Section.ASSAY_RUN_SCHEMAS,
};

export const ASSAY_SCHEMA_TYPE_NAMES = {
  ASSAY_REQUEST: getLegacyRequestObjectName({capwords: true}),
  ASSAY_RESULT: MODEL_NAMES.Result,
  ASSAY_RUN: MODEL_NAMES.Run,
};

export const ASSAY_SCHEMA_TYPE_TO_GQL_TYPENAME: {
  [type in LegacyAssaySchemaType]: AssaySchemaGraphQL['__typename'];
} = {
  [LegacyAssaySchemaType.ASSAY_REQUEST]: 'RequestSchemaNode',
  [LegacyAssaySchemaType.ASSAY_RESULT]: 'ResultSchemaNode',
  [LegacyAssaySchemaType.ASSAY_RUN]: 'RunSchemaNode',
};

export const PRIMARY_SAMPLE_TYPES = {
  NONE: 'NONE',
  ENTITY: 'ENTITY',
  BATCH: 'BATCH',
};

export const ASSAY_SCHEMA_SUMMARY_SHAPE = PropTypes.shape({
  ...ARCHIVABLE_ITEM_REST_PROPS,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  sqlIdentifier: PropTypes.string.isRequired,
});

const assaySchemaShape = {
  ...ARCHIVABLE_ITEM_REST_PROPS,
  id: PropTypes.string.isRequired,
  derivedParent: ASSAY_SCHEMA_SUMMARY_SHAPE,
  fields: PropTypes.arrayOf(SchemaFieldConstants.SCHEMA_FIELD_SHAPE.isRequired).isRequired,
  name: PropTypes.string.isRequired,
  organization: OrganizationConstants.ORGANIZATION.isRequired,
  requestTaskSchemaIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  sampleGroupSchema: SAMPLE_GROUP_SCHEMA_SHAPE,
  schemaType: PropTypes.oneOf(Object.values(LegacyAssaySchemaType)).isRequired,
  sqlIdentifier: PropTypes.string.isRequired,
  publishedDataTableColumns: PropTypes.arrayOf(DATA_TABLE_COLUMN_SHAPE.isRequired),
};

interface AssaySchemaSummary {
  id: string;
  name: string;
  sqlIdentifier: string;
}

export interface AssaySchema extends AssaySchemaSummary, Schema {
  derivedParent: AssaySchemaSummary | null;
  organization: OrganizationConstants.Organization;
  prefix: string | null;
  publishedDataTableColumns: Optional<Array<DataTableColumn>>;
  requestTaskSchemaIds: Array<string>;
  requestTemplateIds: Array<string>;
  sampleGroupSchema?: Optional<SampleGroupSchema>;
  schemaType: LegacyAssaySchemaType;
}

export interface AssayRunSchema extends AssaySchema, HasPoliceablePermissions {
  automationInputFileConfigs: Array<AutomationInputFileConfig>;
  automationOutputFileConfigs: Array<AutomationOutputFileConfig>;
  allowCreateRunFromNlad: boolean;
  allowInsertRunFromInbox: boolean;
  analysisTemplate: {id: string} | null;
  analysisTemplateVariableMapping: JSONScalar | null;
  connectionSchema: {id: string} | null;
  shouldRecordEquipment: boolean;
}

export interface ResultSchema extends AssaySchema, HasPoliceablePermissions {
  schemaType: LegacyAssaySchemaType.ASSAY_RESULT;
}

export const ASSAY_SCHEMA_SHAPE = PropTypes.shape(assaySchemaShape);
